import { Link } from 'react-router-dom';
import { company } from '@/mocks/site';
import BrandLogo from '@/components/feature/BrandLogo';

const heroFacts = [
  { value: '10', label: "Domaines d'expertise" },
  { value: '3', label: 'Métiers historiques' },
  { value: '2', label: 'Bassins couverts' },
];

const cardFacts = [
  { icon: 'ri-hammer-line', label: 'Chaudronnerie' },
  { icon: 'ri-flow-chart', label: 'Tuyauterie process' },
  { icon: 'ri-fire-line', label: 'Soudure TIG · MIG · ARC' },
];

export default function Hero() {
  return (
    <section className="relative flex min-h-[720px] flex-col justify-end overflow-hidden bg-primary-950 pb-10 pt-32 sm:min-h-[820px] md:min-h-[92vh] md:pb-14 md:pt-44">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Deep%20navy%20blue%20industrial%20steel%20workshop%20at%20night%2C%20massive%20welded%20metal%20framework%20and%20large%20process%20pipes%2C%20cold%20blue%20toned%20lighting%20with%20a%20few%20warm%20orange%20welding%20sparks%2C%20dark%20anthracite%20shadows%2C%20high%20contrast%2C%20professional%20industrial%20photography%2C%20no%20people%2C%20atmospheric%20haze&width=1920&height=1080&seq=sgsctic-hero-navy-steel&orientation=landscape&nocache=true"
          alt="Structure métallique industrielle éclairée par la lumière chaude d'un atelier de soudure"
          title="SGSCTIC — travaux industriels à Douala"
          className="hero-zoom h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-950/10 to-primary-950/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 via-primary-950/20 to-transparent" />
        <div className="absolute inset-0 hatch-lines-light opacity-15" />
      </div>

      <div className="container-wide relative z-10">
        <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:items-end lg:gap-16">
          <div className="min-w-0 max-w-3xl">
            <div className="rise flex flex-wrap items-center gap-3" style={{ animationDelay: '120ms' }}>
              <span className="flex items-center gap-2 rounded-full border border-background-50/25 bg-primary-950/30 px-4 py-1.5 backdrop-blur-sm">
                <span className="h-1.5 w-1.5 animate-pulse bg-accent-500" />
                <span className="label-mono text-background-100">Douala · Cameroun</span>
              </span>
              <span className="label-mono text-accent-400">Travaux industriels lourds</span>
            </div>

            <h1
              className="display-title rise mt-7 text-[13vw] leading-[0.86] text-background-50 sm:text-[9vw] lg:text-[5.4rem] xl:text-[6.2rem]"
              style={{ animationDelay: '220ms' }}
            >
              Bâtisseurs de
              <br />
              <span className="text-accent-400">l'industrie</span>
              <br />
              camerounaise
            </h1>

            <div
              className="rise mt-8 max-w-2xl border-l-2 border-accent-500 pl-5"
              style={{ animationDelay: '340ms' }}
            >
              <p className="text-base leading-relaxed text-background-100 md:text-lg">
                {company.baseline} Une équipe de chaudronniers, tuyauteurs et soudeurs au service de
                vos projets les plus exigeants, de l'atelier de Logbaba jusqu'au site.
              </p>
            </div>

            <div className="rise mt-9 flex flex-col gap-3 sm:flex-row" style={{ animationDelay: '460ms' }}>
              <Link
                to="/contact"
                className="btn-shine flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-7 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-foreground-950 transition-colors duration-300 hover:bg-accent-400"
              >
                Demander un devis
                <i className="ri-arrow-right-up-line text-lg" />
              </Link>
              <Link
                to="/offres"
                className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 px-7 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-background-50 transition-colors duration-300 hover:border-background-50 hover:bg-background-50 hover:text-primary-950"
              >
                Découvrir nos expertises
                <i className="ri-arrow-down-line text-lg" />
              </Link>
            </div>
          </div>

          {/* carte d'identité — le bloc-marque intégré au hero */}
          <div className="rise hidden lg:block" style={{ animationDelay: '560ms' }}>
            <div className="relative">
              <div className="pointer-events-none absolute -inset-2.5 rounded-xl border border-background-50/20" />
              <div className="relative overflow-hidden rounded-lg bg-background-50">
                <div className="pointer-events-none absolute inset-0 paper-grid opacity-70" />
                <div className="relative p-6">
                  <BrandLogo markSize={50} tagline="Travaux industriels lourds" />
                  <div className="mt-6 h-px w-full bg-foreground-950/10" />
                  <ul className="mt-5 space-y-3">
                    {cardFacts.map((fact) => (
                      <li key={fact.label} className="flex items-center gap-3">
                        <span className="flex h-7 w-7 flex-none items-center justify-center rounded-sm bg-primary-500 text-background-50">
                          <i className={`${fact.icon} text-sm`} />
                        </span>
                        <span className="text-xs font-medium uppercase tracking-[0.12em] text-foreground-700">
                          {fact.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 flex items-center justify-between gap-3 border-t border-foreground-950/10 pt-4">
                    <span className="label-mono text-foreground-500">Atelier de Logbaba</span>
                    <span className="h-1.5 w-1.5 bg-accent-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rise mt-14 flex flex-col gap-6 border-t border-background-50/15 pt-8 md:mt-16 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-x-12 gap-y-6">
            {heroFacts.map((fact) => (
              <div key={fact.label} className="flex items-baseline gap-3">
                <span className="font-heading text-4xl font-semibold leading-none text-accent-400">
                  {fact.value}
                </span>
                <span className="max-w-[120px] text-[11px] uppercase leading-tight tracking-[0.16em] text-background-300">
                  {fact.label}
                </span>
              </div>
            ))}
          </div>
          <a
            href="#expertises"
            className="group flex cursor-pointer items-center gap-3 text-background-200 transition-colors hover:text-accent-400"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-background-50/30 transition-transform duration-500 group-hover:translate-y-1">
              <i className="ri-arrow-down-line" />
            </span>
            <span className="label-mono">Défiler</span>
          </a>
        </div>
      </div>
    </section>
  );
}